import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { type SubmitEvent, useState } from 'react'

import type { ChangeSuperuserRequest } from '@/bindings/ChangeSuperuserRequest'
import type { AccountIdentity } from '@/bindings/AccountIdentity'
import type { RestaurantDetail } from '@/bindings/RestaurantDetail'
import type { RestaurantStatistics } from '@/bindings/RestaurantStatistics'
import { emptyIdentifierDraft } from '@/features/auth/identifier-draft'
import { Identity } from '@/features/back-office/account-parts'
import { MonthPicker } from '@/features/back-office/month-picker'
import { Loadable } from '@/features/back-office/statistics-page'
import { monthSpan } from '@/features/back-office/statistics-params'
import { AccentScope } from '@/components/accent'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { ConfirmButton } from '@/components/confirm'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { AddBranchForm } from '@/features/restaurants/add-branch-form'
import { DangerCard } from '@/features/restaurants/danger-card'
import { HoldCard } from '@/features/restaurants/hold-card'
import { NoticesCard } from '@/features/restaurants/notices-card'
import { restaurantDetailQuery, restaurantKeys, restaurantPath, restaurantStatisticsQuery } from '@/features/restaurants/api'
import { type SuperuserPick, SuperuserPicker } from '@/features/restaurants/superuser-picker'
import { claim, request } from '@/lib/api'
import { LANGUAGE_NAMES } from '@/lib/i18n/languages'
import { useT } from '@/lib/i18n/translate'
import { todayIn } from '@/lib/time'

const ROUTE_ID = '/platform-auth/restaurants/$restaurantId'

/** Platform edit surface (PRD): deactivate, add branch, change superuser, send a notice, hold or delete (K4); plus the month's order counts. */
export function RestaurantDetailPage() {
  const t = useT()
  const { restaurantId } = useParams({ from: ROUTE_ID })
  const detail = useQuery(restaurantDetailQuery(restaurantId))

  // Data wins over a failed background refetch; only a first load with nothing shows the error.
  const restaurant = detail.data
  if (!restaurant) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {detail.error ? <FormError error={detail.error} /> : <p className="text-muted text-sm">{t('common.loading')}</p>}
        {detail.error && <BackLink />}
      </Page>
    )
  }

  return (
    <AccentScope color={restaurant.color}>
      <Page width="md" className="flex flex-col gap-4">
        <BackLink />
        <OverviewCard restaurant={restaurant} />
        {restaurant.deactivated_at && <HoldCard restaurant={restaurant} />}
        <OrdersCard restaurant={restaurant} />
        <NoticesCard restaurant={restaurant} />
        <SuperuserCard restaurant={restaurant} />
        <BranchesCard restaurant={restaurant} />
        <DangerCard key={`${restaurant.deactivated_at}/${restaurant.hold_until}`} restaurant={restaurant} />
      </Page>
    </AccentScope>
  )
}

function BackLink() {
  const t = useT()
  return (
    <Link to="/restaurants" search={{ page: 1 }} className="text-accent-ink self-start text-sm font-medium hover:underline">
      ← {t('restaurants.title')}
    </Link>
  )
}

/** One idempotent POST (E2); the whole restaurant cache refreshes afterward. */
function useActivityFlip(path: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => request<void>(path, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: restaurantKeys.all }),
  })
}

/**
 * Deactivate needs a second press (PRD: keep destructive actions apart) and
 * reads as destructive from the first one — red, not the restaurant's accent;
 * reactivate is a plain button since nothing is lost by it.
 */
function ActivityButtons({ active, path, label }: { active: boolean; path: string; label: string }) {
  const t = useT()
  const flip = useActivityFlip(`${path}/${active ? 'deactivate' : 'reactivate'}`)

  if (!active) {
    return (
      <Button size="sm" variant="secondary" loading={flip.isPending} onClick={() => flip.mutate()}>
        {t('restaurants.reactivate')}
      </Button>
    )
  }
  return (
    <ConfirmButton
      label={t('restaurants.deactivate')}
      variant="danger-ghost"
      confirmLabel={t('restaurants.confirmDeactivate')}
      confirmAriaLabel={t('restaurants.confirmDeactivateOf', { name: label })}
      align="end"
      confirm={() => flip.mutateAsync()}
    />
  )
}

function OverviewCard({ restaurant }: { restaurant: RestaurantDetail }) {
  const t = useT()
  const active = restaurant.deactivated_at === null
  const languages = [restaurant.main_language, ...restaurant.secondary_languages].map((lang) => LANGUAGE_NAMES[lang])

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span aria-hidden className="bg-accent size-6 shrink-0 rounded-full" />
          <h1 className="truncate text-xl font-semibold">{restaurant.name}</h1>
        </div>
        <Badge tone={active ? 'success' : 'muted'}>{t(active ? 'restaurants.active' : 'restaurants.deactivated')}</Badge>
      </div>
      <dl className="text-muted grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt>{t('restaurants.slug')}</dt>
        <dd className="text-fg">/{restaurant.slug}</dd>
        <dt>{t('restaurants.color')}</dt>
        <dd className="text-fg">{restaurant.color}</dd>
        <dt>{t('language.label')}</dt>
        <dd className="text-fg">{languages.join(', ')}</dd>
        <dt>{t('restaurants.createdAt')}</dt>
        <dd className="text-fg">{new Date(restaurant.created_at).toLocaleDateString()}</dd>
      </dl>
      <div className="flex justify-end">
        <ActivityButtons active={active} path={restaurantPath(restaurant.id)} label={restaurant.name} />
      </div>
    </Card>
  )
}

function BranchesCard({ restaurant }: { restaurant: RestaurantDetail }) {
  const t = useT()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [added, setAdded] = useState<string | undefined>()

  function open() {
    setAdded(undefined)
    setAdding(true)
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('restaurants.branches')}</h2>
        {!adding && (
          <Button size="sm" variant="secondary" onClick={open}>
            {t('restaurants.addBranch')}
          </Button>
        )}
      </div>
      <ul className="flex flex-col gap-2">
        {restaurant.branches.map((branch) => (
          <li key={branch.id} className="border-border flex items-center justify-between gap-3 rounded-lg border p-3">
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2">
                <span className="truncate font-medium">{branch.name}</span>
                {branch.deactivated_at && <Badge tone="muted">{t('restaurants.deactivated')}</Badge>}
              </p>
              <p className="text-muted flex flex-wrap gap-x-3 text-sm">
                <span>/{branch.slug}</span>
                <span>{branch.timezone}</span>
                <span>{branch.currency}</span>
              </p>
            </div>
            <ActivityButtons
              active={branch.deactivated_at === null}
              path={restaurantPath(restaurant.id, `/branches/${branch.id}`)}
              label={branch.name}
            />
          </li>
        ))}
      </ul>
      {added && (
        <p role="status" className="text-muted text-sm">
          {t('restaurants.branchAdded', { name: added })}
        </p>
      )}
      {adding && (
        <AddBranchForm
          path={restaurantPath(restaurant.id, '/branches')}
          siblingSlugs={restaurant.branches.map((branch) => branch.slug)}
          onAdded={(branch) => {
            setAdded(branch.name)
            setAdding(false)
            return queryClient.invalidateQueries({ queryKey: restaurantKeys.all })
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </Card>
  )
}

function SuperuserCard({ restaurant }: { restaurant: RestaurantDetail }) {
  const t = useT()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(() => emptyIdentifierDraft())
  const [pick, setPick] = useState<SuperuserPick>()
  const [changed, setChanged] = useState(false)
  const current = restaurant.superuser

  const change = useMutation({
    mutationFn: (identifier: SuperuserPick['identifier']) => {
      const body: ChangeSuperuserRequest = { identifier }
      return request<AccountIdentity>(restaurantPath(restaurant.id, '/superuser'), { method: 'PUT', body })
    },
    onSuccess: () => {
      setChanged(true)
      setEditing(false)
      return queryClient.invalidateQueries({ queryKey: restaurantKeys.all })
    },
    // The link re-resolves the identifier: an account gone since the lookup drops the pick.
    onError: (error) => {
      if (claim(error, 'ACCOUNT_NOT_FOUND')) setPick(undefined)
    },
  })

  function open() {
    setChanged(false)
    setDraft(emptyIdentifierDraft())
    setPick(undefined)
    change.reset()
    setEditing(true)
  }

  function submit(event: SubmitEvent) {
    event.preventDefault()
    if (pick) change.mutate(pick.identifier)
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('restaurants.superuser')}</h2>
        {!editing && (
          <Button size="sm" variant="secondary" onClick={open}>
            {t(current ? 'restaurants.changeSuperuser' : 'restaurants.linkSuperuser')}
          </Button>
        )}
      </div>
      {current ? (
        <Identity user={current} />
      ) : (
        <p className="flex items-center gap-2 text-sm">
          <Badge tone="warning">{t('restaurants.noSuperuser')}</Badge>
          <span className="text-muted">{t('restaurants.noSuperuserHint')}</span>
        </p>
      )}
      {changed && (
        <p role="status" className="text-muted text-sm">
          {t('restaurants.superuserChanged')}
        </p>
      )}
      {editing && (
        <form onSubmit={submit} className="border-border flex flex-col gap-4 border-t pt-4">
          <p className="text-muted text-sm">{t('restaurants.superuserHint')}</p>
          <SuperuserPicker
            draft={draft}
            onDraftChange={(next) => {
              setDraft(next)
              change.reset()
            }}
            pick={pick}
            onPick={setPick}
          />
          <FormError error={change.error} />
          <div className="flex gap-2">
            <Button type="submit" loading={change.isPending} disabled={!pick}>
              {t(current ? 'restaurants.changeSuperuser' : 'restaurants.linkSuperuser')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}
    </Card>
  )
}

/**
 * The usage view (2026-09-17): the month's orders per branch and status, what the per-order
 * price is charged on. The month lives in the URL; the default is this month by the viewer's clock.
 */
function OrdersCard({ restaurant }: { restaurant: RestaurantDetail }) {
  const t = useT()
  const search = useSearch({ from: ROUTE_ID })
  const navigate = useNavigate()
  const month = search.month ?? todayIn(undefined).slice(0, 7)
  const query = useQuery(restaurantStatisticsQuery(restaurant.id, monthSpan(month)))

  return (
    <Card className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('restaurants.orders')}</h2>
      <MonthPicker
        month={month}
        onChange={(month) => void navigate({ to: '/restaurants/$restaurantId', params: { restaurantId: restaurant.id }, search: { month } })}
      />
      <Loadable query={query}>{query.data && <OrdersTable stats={query.data} />}</Loadable>
    </Card>
  )
}

/** Statuses down, branches across, totals on both edges; every cell a count. */
function OrdersTable({ stats }: { stats: RestaurantStatistics }) {
  const t = useT()
  const statuses = stats.branches[0]?.by_status.map((row) => row.status) ?? []
  const countOf = (branch: RestaurantStatistics['branches'][number], status: string) =>
    branch.by_status.find((row) => row.status === status)?.count ?? 0
  const branchTotal = (branch: RestaurantStatistics['branches'][number]) => branch.by_status.reduce((sum, row) => sum + row.count, 0)
  const statusTotal = (status: string) => stats.branches.reduce((sum, branch) => sum + countOf(branch, status), 0)
  const cell = 'py-1.5 pl-2 text-right tabular-nums'

  return (
    <div className="overflow-x-auto">
      <table aria-label={t('restaurants.orders')} className="w-full text-sm">
        <thead className="text-muted text-xs">
          <tr>
            <th scope="col" className="py-1 text-left font-medium" />
            {stats.branches.map((branch) => (
              <th key={branch.branch_id} scope="col" className="py-1 pl-2 text-right font-medium">
                {branch.name}
                {branch.deactivated_at && <span className="block font-normal">{t('restaurants.deactivated')}</span>}
              </th>
            ))}
            <th scope="col" className="py-1 pl-2 text-right font-medium">
              {t('stats.total')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-border divide-y">
          {statuses.map((status) => (
            <tr key={status}>
              <th scope="row" className="py-1.5 pr-2 text-left font-normal">
                {t(`track.status.${status}`)}
              </th>
              {stats.branches.map((branch) => (
                <td key={branch.branch_id} className={cell}>
                  {countOf(branch, status)}
                </td>
              ))}
              <td className={`${cell} font-medium`}>{statusTotal(status)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-border border-t font-medium">
          <tr>
            <th scope="row" className="py-1.5 pr-2 text-left">
              {t('stats.total')}
            </th>
            {stats.branches.map((branch) => (
              <td key={branch.branch_id} className={cell}>
                {branchTotal(branch)}
              </td>
            ))}
            <td className={cell}>{stats.branches.reduce((sum, branch) => sum + branchTotal(branch), 0)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
